import { useEffect, useState } from "react";
import { BellRing, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";

const WD = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" });
const EMPTY = { origin: "", destination: "", date_from: today(), date_to: today(), weekdays: [], discount_pct: 20, max_seats: "", active: true };
const fmt = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
const sel = "h-11 w-full rounded-xl border border-black/10 bg-white px-3 text-sm";

const Field = ({ label, children }) => <label className="text-sm"><span className="mb-1 block text-[var(--muted-ink)]">{label}</span>{children}</label>;

const PromoForm = ({ cities, onSaved, prefill }) => {
  const [f, setF] = useState(() => ({ ...EMPTY, ...(prefill || {}) }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (i) => setF({ ...f, weekdays: f.weekdays.includes(i) ? f.weekdays.filter((x) => x !== i) : [...f.weekdays, i] });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/fare-promos", { ...f, discount_pct: Number(f.discount_pct), max_seats: f.max_seats ? Number(f.max_seats) : null }); toast.success("Promo creada"); setF(EMPTY); onSaved(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="fare-promo-form" className="grid gap-3 rounded-3xl bg-white p-5 sm:grid-cols-2 lg:grid-cols-3">
      <Field label="Desde"><select required value={f.origin} onChange={set("origin")} data-testid="fare-promo-origin" className={sel}><option value="">Elegí</option>{cities.map((c) => <option key={c}>{c}</option>)}</select></Field>
      <Field label="Hasta"><select required value={f.destination} onChange={set("destination")} data-testid="fare-promo-destination" className={sel}><option value="">Elegí</option>{cities.filter((c) => c !== f.origin).map((c) => <option key={c}>{c}</option>)}</select></Field>
      <Field label="Descuento (%)"><Input type="number" min="5" max="80" required value={f.discount_pct} onChange={set("discount_pct")} data-testid="fare-promo-pct" className="h-11 rounded-xl" /></Field>
      <Field label="Viajes desde el"><Input type="date" min={today()} required value={f.date_from} onChange={set("date_from")} data-testid="fare-promo-from" className="h-11 rounded-xl" /></Field>
      <Field label="Hasta el"><Input type="date" min={f.date_from} required value={f.date_to} onChange={set("date_to")} data-testid="fare-promo-to" className="h-11 rounded-xl" /></Field>
      <div className="text-sm"><span className="mb-1 block text-[var(--muted-ink)]">Solo estos días (vacío = todos)</span>
        <div className="flex flex-wrap gap-1">{WD.map((w, i) => (
          <button key={w} type="button" onClick={() => toggle(i)} data-testid={`fare-promo-wd-${i}`} className={`h-11 w-11 rounded-xl text-sm font-bold ${f.weekdays.includes(i) ? "bg-[var(--forest)] text-white" : "bg-black/5"}`}>{w}</button>
        ))}</div>
      </div>
      <Field label="Butacas con promo por viaje (vacío = sin límite)"><Input type="number" min="1" max="80" value={f.max_seats} onChange={set("max_seats")} placeholder="Sin límite" data-testid="fare-promo-max-seats" className="h-11 rounded-xl" /></Field>
      <button disabled={busy} data-testid="fare-promo-save" className="h-11 rounded-xl bg-[var(--forest)] px-6 font-bold text-white disabled:opacity-60 sm:col-span-2 lg:col-span-2 lg:w-fit lg:self-end">{busy ? "Guardando..." : "Crear promo"}</button>
    </form>
  );
};

const PromoRow = ({ p, onChange }) => {
  const body = ({ id, alerts, created_at, created_by, ...rest }) => rest;
  const toggle = async () => {
    try { await api.put(`/admin/fare-promos/${p.id}`, { ...body(p), active: !p.active }); onChange(); } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    if (!window.confirm("¿Borrar esta promo?")) return;
    try { await api.delete(`/admin/fare-promos/${p.id}`); onChange(); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div data-testid={`fare-promo-${p.id}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-4 text-sm ${p.active ? "" : "opacity-60"}`}>
      <p><span className="mr-2 rounded-full bg-[var(--ink)] px-2 py-0.5 text-xs font-bold text-[var(--lime)]">PROMO {p.discount_pct}% OFF</span>
        <b>{p.origin} → {p.destination}</b> · {fmt(p.date_from)} al {fmt(p.date_to)}{p.weekdays?.length ? ` · ${p.weekdays.map((i) => WD[i]).join(", ")}` : ""}{p.max_seats ? ` · ${p.max_seats} butacas por viaje` : " · sin límite de butacas"}
        <span className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--muted-ink)]"><BellRing className="h-3.5 w-3.5" /> {p.alerts ?? 0} esperando aviso</span></p>
      <div className="flex gap-2">
        <button onClick={toggle} data-testid={`fare-promo-toggle-${p.id}`} className="rounded-full bg-black/5 px-4 py-1.5 font-bold">{p.active ? "Pausar" : "Activar"}</button>
        <button onClick={remove} data-testid={`fare-promo-delete-${p.id}`} aria-label="Borrar promo" className="rounded-full bg-red-50 p-2 text-red-700"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const ars = (n) => `$${Number(n || 0).toLocaleString("es-AR")}`;

/** Resultados por promo: pasajes vendidos, butacas con descuento, cobrado y descontado. */
export const PromoResults = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/reports/fare-promos").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(apiError(e)); setRows([]); }); }, []);
  if (!rows) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!rows.length) return <p data-testid="promo-results-empty" className="text-sm text-[var(--muted-ink)]">Todavía no hay promos creadas.</p>;
  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
  return (
    <div data-testid="report-promo-results" className="overflow-x-auto rounded-3xl bg-white p-5">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead><tr className="text-[var(--muted-ink)]">{["Promo", "Fechas", "Pasajes pagados", "Butacas en promo", "Reservas sin pagar", "Cobrado", "Descontado"].map((h) => <th key={h} className="pb-2 pr-3">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`promo-result-${r.id}`} className="border-t border-black/5">
              <td className="py-2 pr-3"><b>{r.origin} → {r.destination}</b> · {r.discount_pct}% {r.active ? "" : <span className="text-xs text-[var(--muted-ink)]">(pausada)</span>}</td>
              <td className="py-2 pr-3">{fmt(r.date_from)} al {fmt(r.date_to)}</td>
              <td className="py-2 pr-3 font-semibold">{r.paid_tickets}</td>
              <td className="py-2 pr-3">{r.promo_seats}</td>
              <td className="py-2 pr-3">{r.pending_tickets}</td>
              <td className="py-2 pr-3 font-semibold">{ars(r.revenue)}</td>
              <td className="py-2 pr-3 text-red-700">-{ars(r.discount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr data-testid="promo-results-total" className="border-t-2 border-black/10 font-bold">
          <td className="pt-2" colSpan={2}>Total</td><td className="pt-2">{sum("paid_tickets")}</td><td className="pt-2">{sum("promo_seats")}</td>
          <td className="pt-2">{sum("pending_tickets")}</td><td className="pt-2">{ars(sum("revenue"))}</td><td className="pt-2 text-red-700">-{ars(sum("discount"))}</td>
        </tr></tfoot>
      </table>
      <p className="mt-3 text-xs text-[var(--muted-ink)]">Cuenta pasajes pagados (confirmados o ya usados). El descontado es la diferencia contra el precio normal en las butacas que entraron en la promo.</p>
    </div>
  );
};

export const FarePromosAdmin = ({ prefill }) => {
  const [cities, setCities] = useState([]);
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/fare-promos").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(apiError(e)); setList([]); });
  useEffect(() => { api.get("/cities").then((r) => setCities(r.data?.cities ?? [])).catch(() => {}); load(); }, []);
  return (
    <div data-testid="report-fare-promos" className="space-y-4">
      <p className="text-sm text-[var(--muted-ink)]">Elegí un tramo y los días de viaje con descuento. El precio baja solo en la web y en boletería, el calendario los marca como PROMO y avisamos por email a quienes pidieron alerta de ese tramo.</p>
      <PromoForm cities={cities} onSaved={load} prefill={prefill} />
      {!list ? <Loader2 className="h-5 w-5 animate-spin" /> : list.length === 0
        ? <p data-testid="fare-promos-empty" className="text-sm text-[var(--muted-ink)]">Todavía no hay promos.</p>
        : list.map((p) => <PromoRow key={p.id} p={p} onChange={load} />)}
    </div>
  );
};
